import type { ReactNode } from 'react'
import { Button } from '@systemeio/components'
import { useTranslation } from 'next-i18next/pages'
import { useRouter } from 'next/router'
import { AccessDenied } from 'shared/errors/access-denied'
import { UnauthorizedResponse } from 'shared/errors/unauthorized-response'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { useJoinCommunity } from '../api/use-join-community'
import { useCommunityWithTopics } from '../hooks/use-community-with-topics'
import useIsUserMember from '../hooks/use-is-user-member'

function SidebarJoin(): ReactNode {
  const router = useRouter()
  const { t } = useTranslation()
  const { communityPath } = router.query
  const { isValidating: isCommunityLoading } = useCommunityWithTopics()
  const { mutate } = useIsUserMember()
  const { user } = useUser()
  const joinCommunityMutation = useJoinCommunity()

  async function join(): Promise<void> {
    if (!communityPath) return
    try {
      if (isAuthorizedUser(user)) {
        await joinCommunityMutation.mutateAsync(communityPath)
        await mutate()
      } else {
        // the public community has an own login form, and we need to make custom redirection
        await router.replace(`/community/${communityPath}/join`)
      }
    } catch (error) {
      if (error instanceof UnauthorizedResponse) {
        await router.replace(`/community/${communityPath}/join`)
      } else if (error instanceof AccessDenied) {
        await router.replace(`/community/403`)
      }
    }
  }

  if (isCommunityLoading) {
    return (
      <div className="bg-bg-primary shadow-1">
        <div className="mt-5 size-6 animate-pulse rounded-xs bg-bg-tertiary" />
      </div>
    )
  }

  return (
    <div className="flex w-full justify-center">
      <Button variant="primary" onClick={join} className="w-full">
        {t('join_form.button.join.title')}
      </Button>
    </div>
  )
}

export default SidebarJoin
